import { getCsrfValue } from "~/common/utils/ApiHelper";
import { resolveApiUrl } from "~/utils/url/urlResolver";

export interface ConfiguratorAttachmentApiResponse {
  success: boolean;
  fileName?: string;
  url?: string;
  fileSizeBytes?: number;
  uploadedAt?: string | number;
  errorMessage?: string;
}

export class ConfiguratorAttachmentApiError extends Error {
  readonly status: number;
  readonly response?: ConfiguratorAttachmentApiResponse;

  constructor(status: number, response?: ConfiguratorAttachmentApiResponse) {
    super(`Configurator attachment request failed with status ${status}.`);
    this.name = "ConfiguratorAttachmentApiError";
    this.status = status;
    this.response = response;
  }
}

const attachmentPath = (productCode: string, operation: "upload" | "delete") =>
  `/${encodeURIComponent(productCode)}/configuratorPage/LinearConfigurator/doc/${operation}`;

const readResponse = async (response: Response) => {
  try {
    return (await response.json()) as ConfiguratorAttachmentApiResponse;
  } catch {
    return undefined;
  }
};

export const uploadConfiguratorAttachment = async (
  productCode: string,
  configId: string,
  file: File,
  signal?: AbortSignal,
): Promise<ConfiguratorAttachmentApiResponse> => {
  const body = new FormData();
  body.append("file", file);
  body.append("configId", configId);
  const response = await fetch(resolveApiUrl(attachmentPath(productCode, "upload")), {
    body,
    credentials: "same-origin",
    headers: { Accept: "application/json", CSRFToken: getCsrfValue() },
    method: "POST",
    ...(signal ? { signal } : {}),
  });
  const payload = await readResponse(response);
  if (!response.ok) {
    throw new ConfiguratorAttachmentApiError(response.status, payload);
  }
  return payload ?? { success: true };
};

export const deleteConfiguratorAttachment = async (
  productCode: string,
  configId: string,
  signal?: AbortSignal,
): Promise<void> => {
  const params = new URLSearchParams({ configId });
  const response = await fetch(`${resolveApiUrl(attachmentPath(productCode, "delete"))}?${params.toString()}`, {
    credentials: "same-origin",
    headers: { Accept: "application/json", CSRFToken: getCsrfValue() },
    method: "DELETE",
    ...(signal ? { signal } : {}),
  });
  if (!response.ok) {
    throw new ConfiguratorAttachmentApiError(response.status, await readResponse(response));
  }
};
